/**
 * Map free-typed command-bar input → analyze/product API params + route href.
 * Mirrors backend parse_analyze_identifier classification.
 */

/**
 * GS1 check digit (same weighting as backend app/gtin.py).
 * @param {string} body digits without check digit
 */
export function gtinCheckDigit(body) {
  const s = String(body || "");
  if (!s || !/^\d+$/.test(s)) return null;
  let total = 0;
  for (let i = 0; i < s.length; i += 1) {
    const n = Number(s[s.length - 1 - i]);
    total += n * (i % 2 === 0 ? 3 : 1);
  }
  return String((10 - (total % 10)) % 10);
}

/**
 * @param {string} raw digit string of length 8, 12, 13, or 14
 */
export function isValidGtinDigits(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (![8, 12, 13, 14].includes(digits.length)) return false;
  const body = digits.slice(0, -1);
  const expected = gtinCheckDigit(body);
  return expected != null && expected === digits.slice(-1);
}

/**
 * Classify a free-typed identifier for Analyzer + Product Finder.
 * @param {string} raw
 * @returns {{ path: string, params: Record<string,string>, note?: string } | null}
 */
export function classifyProductIdentifier(raw) {
  const value = String(raw ?? "").trim();
  if (!value) return null;

  const lower = value.toLowerCase();
  if (lower.includes("walmart.com") || lower.includes("/ip/") || /^https?:\/\//i.test(value)) {
    return {
      path: "walmart_url",
      params: { url: value },
      note: "Walmart product URL → item id",
    };
  }

  const digits = value.replace(/\D/g, "");
  const pureDigits = value === digits && digits.length > 0;

  if (pureDigits && isValidGtinDigits(digits)) {
    return {
      path: "gtin_upc",
      params: { upc: digits },
      note: "Valid GTIN/UPC check digit → offers + catalog",
    };
  }

  // 8–12 digits failing check → Walmart item id; also classic 5–7 digit item ids.
  if (pureDigits && digits.length >= 5 && digits.length <= 12) {
    return {
      path: "walmart_item_id",
      params: { itemId: digits },
      note:
        digits.length <= 7
          ? "Walmart item id"
          : "Failed GTIN check digit → tried as Walmart item id",
    };
  }

  if (pureDigits && digits.length > 12) {
    return {
      path: "invalid_gtin",
      params: { upc: digits },
      note: "Invalid GTIN check digit",
    };
  }

  return {
    path: "free_text",
    params: { q: value },
    note: "Free-text query",
  };
}

/** @param {string} raw */
export function buildAnalyzeParams(raw) {
  const classified = classifyProductIdentifier(raw);
  if (!classified) return null;
  return classified.params;
}

/** @param {string} raw */
export function analyzerHref(raw) {
  const classified = classifyProductIdentifier(raw);
  if (!classified) return null;
  const params = classified.params;
  const qs = new URLSearchParams();
  if (params.itemId) qs.set("itemId", params.itemId);
  else if (params.url) qs.set("url", params.url);
  else if (params.upc) qs.set("upc", params.upc);
  else if (params.q) qs.set("q", params.q);
  const s = qs.toString();
  return s ? `/app/analyzer?${s}` : "/app/analyzer";
}

/**
 * Resolve live API params from the current location search string.
 * Empty search → null (launch screen; demo is opt-in via ?demo=1).
 * @param {string} search location.search including leading ?
 */
export function liveParamsFromSearch(search) {
  const sp = new URLSearchParams(search || "");
  const itemId = (sp.get("itemId") || "").trim();
  const url = (sp.get("url") || "").trim();
  const upc = (sp.get("upc") || "").trim();
  const q = (sp.get("q") || "").trim();
  if (itemId) return { itemId };
  if (url) return { url };
  if (upc) return { upc };
  if (q) return { q };
  return null;
}

/**
 * Opt-in fixture demo (`?demo=1`). Never implied by bare /app/analyzer.
 * @param {string} search
 */
export function isDemoSearch(search) {
  const sp = new URLSearchParams(search || "");
  const v = (sp.get("demo") || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

/** Launch landing (no live identifier and not demo). */
export function isAnalyzerLaunchSearch(search) {
  return !liveParamsFromSearch(search) && !isDemoSearch(search);
}

/** Display string for command bar when location already has analyzer params. */
export function displayQueryFromSearch(search) {
  const p = liveParamsFromSearch(search);
  if (!p) return "";
  return p.itemId || p.url || p.upc || p.q || "";
}

/**
 * Human label for lookup path (API or client classification).
 * @param {string|null|undefined} path
 */
export function pathLabel(path) {
  switch (path) {
    case "gtin_upc":
      return "Looked up via UPC/GTIN (valid check digit)";
    case "walmart_item_id":
      return "Looked up as Walmart item id";
    case "walmart_url":
      return "Looked up from Walmart product URL";
    case "offers_index":
      return "Sourced from offers index";
    case "fixture_demo":
      return "Demo fixture (no identifier)";
    case "asin":
      return "Looked up via ASIN → GTIN map";
    case "invalid_gtin":
      return "Invalid GTIN";
    default:
      return path ? String(path) : null;
  }
}

/**
 * Unwrap `/analyze/product` envelope into product + meta, or not-found shape.
 * @param {any} envelope
 * @returns {{ product: object, dataMode: string|null, path: string|null, resolved: boolean } | null}
 */
export function productFromEnvelope(envelope) {
  if (!envelope || typeof envelope !== "object") return null;
  const data = envelope.data;
  if (!data || typeof data !== "object") return null;

  // Explicit catalog miss (200 + resolved:false, data_mode live).
  if (data.resolved === false) {
    return {
      product: null,
      dataMode: envelope.data_mode || "live",
      path: data.path || null,
      resolved: false,
      reason: data.reason || null,
      message: data.message || null,
      identifier: data.identifier || null,
    };
  }

  const product = data.product;
  if (!product || typeof product !== "object") return null;
  if (!product.calc_input || typeof product.calc_input !== "object") return null;
  return {
    product,
    dataMode: envelope.data_mode || null,
    path: data.path || product?.data_quality?.query?.path || null,
    resolved: data.resolved !== false,
    identifier: data.identifier || null,
    offers: data.offers || null,
  };
}

/**
 * When searching by itemId, reject a mismatched catalog id so a stale/wrong body
 * never silently sticks as the displayed product.
 *
 * Exact digit-normalized equality only — SKU must never satisfy item-id match,
 * and suffix/prefix fuzzy matches are intentionally disallowed.
 * @param {object|null|undefined} product
 * @param {{ itemId?: string, url?: string, upc?: string, q?: string }|null} params
 */
export function productMatchesLiveParams(product, params) {
  if (!params?.itemId) return true;
  if (!product) return false;
  const want = String(params.itemId).replace(/\D/g, "");
  if (!want) return true;
  const w = product.walmart || {};
  const candidates = [
    product.id,
    w.item_id_clean,
    w.walmart_item_id,
  ]
    .filter((v) => v != null && v !== "")
    .map((v) => String(v).replace(/\D/g, ""))
    .filter(Boolean);
  if (!candidates.length) return true; // incomplete payload — don't block on match
  return candidates.some((c) => c === want);
}
